import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";
import type { PageSettings, ReportParameterConfig, SpreadsheetColumn } from "~reports/types";

import isRecord from "~/helpers/isRecord";

export const REPORT_BUILDER_SOURCE_KIND = "reportBuilder";

export const REPORT_TARGET_KIND = "report";

const REPORT_BUILDER_ROUTE_PATTERN = /^\/reports\/builder\/([^/?#]+)$/;

export interface ReportBuilderSourceCheckpoint {
	craftJsTemplate: string;
	filterDraft: Record<string, unknown>;
	filters: Record<string, unknown>;
	hasUnsavedChanges: boolean;
	pageSettings: PageSettings;
	parameterConfig: ReportParameterConfig[];
	reportId: string;
	reportName: string;
	spreadsheetColumns?: SpreadsheetColumn[];
}

const isPageSettings = (value: unknown): value is PageSettings =>
	isRecord(value) && isRecord(value.margins) && isRecord(value.size) && typeof value.orientation === "string";

const isParameterConfig = (value: unknown): value is ReportParameterConfig =>
	isRecord(value) && typeof value.name === "string" && typeof value.enabled === "boolean";

export const isReportBuilderSourceCheckpoint = (value: unknown): value is ReportBuilderSourceCheckpoint =>
	isRecord(value) &&
	typeof value.craftJsTemplate === "string" &&
	isRecord(value.filterDraft) &&
	isRecord(value.filters) &&
	typeof value.hasUnsavedChanges === "boolean" &&
	isPageSettings(value.pageSettings) &&
	Array.isArray(value.parameterConfig) &&
	value.parameterConfig.every(isParameterConfig) &&
	typeof value.reportId === "string" &&
	typeof value.reportName === "string";

export const buildReportBuilderPath = (reportId: string): string => `/reports/builder/${encodeURIComponent(reportId)}`;

export const matchReportReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = REPORT_BUILDER_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		return { targetId: decodeURIComponent(match[1]), targetKind: REPORT_TARGET_KIND };
	} catch {
		return null;
	}
};

export const reportReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => buildReportBuilderPath(targetId),
	matchesRoute: matchReportReceiverRoute
};

const reportBuilderEditorKey = (reportId: string): string => `report-builder:${reportId}`;

export default reportBuilderEditorKey;
